import type { BoxProps } from '@withjoy/joykit';
import { Flex, PopoverV2, Box, DrawerV2, TextV2 } from '@withjoy/joykit';
import { Cart, CheckOutline, Registry as RegistryIcon } from '@withjoy/joykit/icons';
import React, { forwardRef, useState, useEffect } from 'react';
import {
  StyledMobileCartCircle,
  StyledTryChecklistBackgroundImage,
  StyledTryChecklistButtonWrapper,
  StyledTryChecklistPrimaryButton,
  StyledTryChecklistSecondaryButton,
  StyledTryChecklistWrapper,
  StyledWidgetTriggerButton,
  StyledWidgetTriggerRoot,
  styles,
  tryChecklistStyles,
} from './RegistryGiftBasketWidget.styles';
import type { RegistryGiftBasketCountByEventNameQuery } from './RegistryGiftBasketWidget.graphql.generated';
import { CatalogStorefront } from '@graphql/generated-base';
import { useRegistryGiftTrackerWidget, useRegistryGiftTrackerWidgetRoot } from './RegistryGiftBasketWidget.controller';
import { StyledCountContainer } from '@apps/registry/common/components/ShoppingCart/components/ShoppingCartOpenButton/ShoppingCartOpenButton.styles';
import { GiftBasketContent } from './components/GiftBasketContent';
import { useTranslation } from '@shared/core';
import { RegistryGiftBasketWidgetTelemetryProvider } from './RegistryGiftBasketWidget.telemetry';
import { pxToRem } from '@withjoy/joykit/theme';
import { GiftBasketFloatingWidget } from './components/GiftBasketFloatingWidget';
import { useEnableChecklist } from './hooks';
import { useShopStorefrontContext } from '../Catalog/components/ShopStorefrontProvider';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { useRegistryShopping } from '@apps/registry/admin/RegistryShopping.provider';
import { useShoppingCart } from '../ShoppingCart/state';
import { ProgressRing } from '../RegistryGiftCounts/components';
import { useRegistryGiftCounts } from '../RegistryGiftCounts/RegistryGiftCounts.controller';
import { Page } from '../RegistryGiftCounts/telemetry';

const TRY_CHECKLIST_IMG_URL = 'https://withjoy.com/assets/public/apps/registry/checklist/try-checklist@2x.jpg';
const TRY_CHECKLIST_BABY_IMG_URL = 'https://withjoy.com/assets/public/apps/registry/checklist/try_checklist_baby.png';

interface GiftTrackerWidgetProps
  extends Readonly<{
    eventId: string;
    eventHandle: string;
    showFloatingPill?: boolean;
    hideChecklistPopover?: boolean;
    hideChecklistTrigger?: boolean;
  }> {}

interface WidgetButtonProps extends BoxProps<'button'> {
  isMobile?: boolean;
}

const WidgetButton: React.FC<WidgetButtonProps> = ({ isMobile, ...props }) => {
  return (
    <StyledWidgetTriggerButton
      as="button"
      __css={isMobile ? styles.widgetTriggerMobileButton : styles.widgetTriggerButton}
      {...props}
    />
  );
};

const EMOJIS = ['🎉', '✨', '😎', '🥂', '🪩', '🤸', '🚀', '💪', '🔥'];

export const WidgetTrigger: React.FC<
  BoxProps<'button'> & {
    onTriggerClick: React.MouseEventHandler<HTMLButtonElement>;
    registryItemCount: number;
    trackedGiftsCount: number;
    completionPercent: ReturnType<typeof useRegistryGiftCounts>['completionPercent'];
  }
> = forwardRef((props, ref) => {
  const { onClick, onTriggerClick, trackedGiftsCount, registryItemCount, completionPercent, ...restProps } = props;
  const {
    state: { itemListCount: cartItemsCount },
  } = useShoppingCart();
  const enableChecklistInGiftBasketWidget = useEnableChecklist();
  const { isEnabled: isShoppingEnabled } = useRegistryShopping();
  const isMobile = useIsMobileScreen();
  const { t2 } = useTranslation('sharedComponents');
  const { checklist } = t2('registryGiftBasket', 'floatingWidget');
  const [emojiIndex, setEmojiIndex] = useState(0);
  const [showGiftIcon, setShowGiftIcon] = useState(false);

  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    const initialTimer = setTimeout(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setShowGiftIcon(true);
        setIsTransitioning(false);
      }, 200);
    }, 3000);

    return () => clearTimeout(initialTimer);
  }, []);

  useEffect(() => {
    if (completionPercent >= 100) {
      const interval = setInterval(async () => {
        setIsTransitioning(true);
        await new Promise(resolve => setTimeout(resolve, 200));

        setShowGiftIcon(prev => !prev);
        if (!showGiftIcon) {
          setEmojiIndex(prevIndex => (prevIndex + 1) % EMOJIS.length);
        }

        await new Promise(resolve => setTimeout(resolve, 30));
        setIsTransitioning(false);

        if (!showGiftIcon) {
          setTimeout(() => {
            setIsTransitioning(true);
          }, 800);
        }
      }, 3000);

      return () => clearInterval(interval);
    }
    return () => {};
  }, [completionPercent, showGiftIcon]);

  return (
    <StyledWidgetTriggerRoot
      ref={ref}
      __css={isMobile ? {} : styles.widgetTriggerRoot}
      height="100%"
      {...restProps}
    >
      {!isMobile &&
        (enableChecklistInGiftBasketWidget ? (
          <WidgetButton
            data-tab="checklist"
            onClick={onTriggerClick}
          >
            <CheckOutline />
            {checklist}
          </WidgetButton>
        ) : (
          <WidgetButton
            data-tab="giftTracker"
            onClick={onTriggerClick}
          >
            {trackedGiftsCount}
          </WidgetButton>
        ))}
      <WidgetButton
        data-tab="giftList"
        onClick={onTriggerClick}
        isMobile={isMobile}
      >
        <ProgressRing
          progressPercent={completionPercent}
          isOpen={true}
          showCheck={false}
          dimensions={{
            width: 40,
            height: 40,
            cx: 20,
            cy: 20,
            r: 18,
            strokeWidth: 3,
          }}
        >
          <Box
            style={{
              opacity: isTransitioning ? 0 : 1,
              transition: 'opacity 0.2s ease',
            }}
          >
            {showGiftIcon ? (
              <RegistryIcon
                style={{
                  margin: 0,
                  width: 16,
                  height: 16,
                  marginRight: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              />
            ) : (
              EMOJIS[emojiIndex]
            )}
          </Box>
        </ProgressRing>
      </WidgetButton>
      {isShoppingEnabled
        ? !isMobile && (
            <WidgetButton
              data-tab="shoppingCart"
              onClick={onTriggerClick}
            >
              {isMobile ? (
                <>
                  <Cart style={{ marginRight: 0 }} />
                  {cartItemsCount > 0 && <StyledMobileCartCircle />}
                </>
              ) : (
                <>
                  <Cart />
                  {cartItemsCount > 0 && (
                    <StyledCountContainer __css={styles.cartCountContainer}>
                      <TextV2 typographyVariant="label3">{cartItemsCount > 99 ? '99+' : cartItemsCount}</TextV2>
                    </StyledCountContainer>
                  )}
                </>
              )}
            </WidgetButton>
          )
        : null}
    </StyledWidgetTriggerRoot>
  );
});

const RegistryGiftBasketWidgetRoot: React.FC<{
  data: NonNullable<RegistryGiftBasketCountByEventNameQuery['eventByName']>;
  showFloatingPill?: boolean;
  hideChecklistPopover?: boolean;
  hideChecklistTrigger?: boolean;
  eventHandle: string;
}> = props => {
  const {
    currentTab,
    eventId,
    handleTriggerClick,
    handleOnVisitManageTabClick,
    handleOnSegmentedTabClick,
    handleOnTryChecklist,
    isOpen,
    isMobileOrTablet,
    onClose,
    registryItemsDto,
    registryOrdersDto,
    tryChecklistPopoverIsOpen,
    handleOnNoThanksToChecklist,
    isCheckoutInitiated,
  } = useRegistryGiftTrackerWidgetRoot(props);

  const { t2 } = useTranslation('registryChecklist');
  const { title, subtitle, primaryButton, secondaryButton } = t2('zeroState');
  const enableChecklistInGiftBasketWidget = useEnableChecklist();
  const { currentStorefront } = useShopStorefrontContext();
  const CTA_IMAGE = currentStorefront === CatalogStorefront.baby ? TRY_CHECKLIST_BABY_IMG_URL : TRY_CHECKLIST_IMG_URL;
  const { completionPercent } = useRegistryGiftCounts({ eventId, page: Page.overview, eventHandle: props.eventHandle });

  const triggerMarkup = (
    <WidgetTrigger
      onTriggerClick={handleTriggerClick}
      registryItemCount={registryItemsDto.totalRegistryItemsCount}
      trackedGiftsCount={registryOrdersDto.entries.length}
      completionPercent={completionPercent}
    />
  );
  const contentMarkup = (
    <GiftBasketContent
      eventHandle={props.eventHandle}
      eventId={eventId}
      currentTab={currentTab}
      onSegmentedTabClick={handleOnSegmentedTabClick}
      registryItemIds={registryItemsDto.itemIds}
      totalRegistryItemsCount={registryItemsDto.totalRegistryItemsCount}
      registryOrderIds={registryOrdersDto.uniqueOrderIds}
      totalTrackedGiftsCount={registryOrdersDto.entries.length}
      onVisitManageTabClick={handleOnVisitManageTabClick}
      onCloseDrawer={onClose}
    />
  );

  return (
    <>
      <Flex>
        {isMobileOrTablet && !enableChecklistInGiftBasketWidget ? (
          <>
            {!props.hideChecklistTrigger && (
              <>
                {triggerMarkup}
                <DrawerV2
                  anchor="bottom"
                  size={1}
                  isOpen={isOpen}
                  onClose={onClose}
                >
                  <DrawerV2.CloseButton />
                  <DrawerV2.Body padding={0}>{contentMarkup}</DrawerV2.Body>
                </DrawerV2>
              </>
            )}
          </>
        ) : enableChecklistInGiftBasketWidget ? (
          <>
            {!props.hideChecklistTrigger ? (
              <PopoverV2
                isOpen={tryChecklistPopoverIsOpen}
                onClose={handleOnNoThanksToChecklist}
                placement="bottomEnd"
                showArrow={true}
              >
                <PopoverV2.Anchor>{triggerMarkup}</PopoverV2.Anchor>
                <PopoverV2.Content
                  width={pxToRem(328)}
                  padding={6}
                  borderRadius={pxToRem(8)}
                >
                  {/* We need to keep this inner code here and not in a separate component so it's mounted at the same time to avoid issues when opening as it does it automatically */}
                  <StyledTryChecklistWrapper>
                    <StyledTryChecklistBackgroundImage
                      __css={tryChecklistStyles.backgroundImage}
                      backgroundImage={`url(${CTA_IMAGE})`}
                    />
                    <TextV2
                      typographyVariant="hed1"
                      marginBottom={1}
                    >
                      {title}
                    </TextV2>
                    <TextV2
                      typographyVariant="body1"
                      marginBottom={6}
                    >
                      {subtitle}
                    </TextV2>
                    <StyledTryChecklistButtonWrapper __css={tryChecklistStyles.buttonWrapper}>
                      <StyledTryChecklistPrimaryButton
                        as="button"
                        typographyVariant="label3"
                        __css={tryChecklistStyles.primaryButton}
                        onClick={handleOnTryChecklist}
                      >
                        {primaryButton}
                      </StyledTryChecklistPrimaryButton>
                      <StyledTryChecklistSecondaryButton
                        color="mono12"
                        variant="link"
                        typographyVariant="label3"
                        onClick={handleOnNoThanksToChecklist}
                      >
                        {secondaryButton}
                      </StyledTryChecklistSecondaryButton>
                    </StyledTryChecklistButtonWrapper>
                  </StyledTryChecklistWrapper>
                </PopoverV2.Content>
              </PopoverV2>
            ) : null}
            <DrawerV2
              size={isMobileOrTablet || isCheckoutInitiated ? 1 : 400}
              isOpen={isOpen}
              useBackdrop={false}
              onClose={onClose}
              overrides={{
                Content: {
                  props: {
                    transition: 'width 0.5s ease-in-out',
                  },
                },
                Body: {
                  props: {
                    paddingX: 0,
                    paddingY: 0,
                    paddingTop: '3.5rem',
                  },
                },
              }}
            >
              {!isCheckoutInitiated && (
                <DrawerV2.CloseButton
                  color="mono14"
                  _hover={{ color: 'mono12' }}
                  _active={{ color: 'mono13' }}
                />
              )}
              <DrawerV2.Body overflowY="scroll">{contentMarkup}</DrawerV2.Body>
            </DrawerV2>
          </>
        ) : (
          <PopoverV2
            placement="bottomEnd"
            isOpen={isOpen}
            onClose={onClose}
          >
            <PopoverV2.Trigger>{triggerMarkup}</PopoverV2.Trigger>

            <PopoverV2.Content borderRadius={'12px'}>{contentMarkup}</PopoverV2.Content>
          </PopoverV2>
        )}
      </Flex>

      {!isMobileOrTablet && enableChecklistInGiftBasketWidget && props.showFloatingPill && (
        <GiftBasketFloatingWidget
          onTriggerClick={handleTriggerClick}
          registryItemCount={registryItemsDto.totalRegistryItemsCount}
        />
      )}
    </>
  );
};

export const RegistryGiftBasketWidget: React.FC<GiftTrackerWidgetProps> = props => {
  const { data, loading } = useRegistryGiftTrackerWidget(props);

  if (loading || !data) {
    return null;
  }

  return (
    <RegistryGiftBasketWidgetTelemetryProvider context={{ eventId: props.eventId }}>
      <RegistryGiftBasketWidgetRoot
        data={data}
        showFloatingPill={props.showFloatingPill}
        hideChecklistPopover={props.hideChecklistPopover}
        hideChecklistTrigger={props.hideChecklistTrigger}
        eventHandle={props.eventHandle}
      />
    </RegistryGiftBasketWidgetTelemetryProvider>
  );
};

export const RegistryGiftBasketWidgetBanner: React.FC<GiftTrackerWidgetProps> = props => {
  const content = <RegistryGiftBasketWidget {...props} />;

  if (content) {
    return <Box __css={styles.widgetBanner}>{content}</Box>;
  }

  return null;
};
